/**
 * views/FiscalHealthView.js
 * ----------------------------------------------------------------------------
 * The EFRIS readiness report, GET /api/fiscal-health, laid out so a person can
 * answer "can Lusuku file to URA right now, and if not, why" at a glance, in
 * front of URA if need be.
 *
 * WHY A PAGE FOR A HEALTH ENDPOINT. The fiscal submitter is a separate
 * process on a separate host, with URA credentials, a signing certificate and
 * a clock that all have to be right before a single document can be filed.
 * When filing stops, the first question is "which of those is wrong", and the
 * answer used to need a shell on the submitter's box.
 *
 * WHAT IS THE SERVER'S AND WHAT IS THIS PAGE'S. Every check, whether it
 * passed, which stage it belongs to and what to do when it fails come from the
 * report (services/fiscal/fiscalReadinessReport.js), the one implementation
 * both this page and scripts/fiscalReadiness.js read. This page groups and
 * colours them and nothing more. A PRODUCTION check failing while the identity
 * is in TEST is shown as "not yet" rather than as a fault, because rehearsing
 * in TEST is the plan, not a problem. Masked values (the TIN, whether a device
 * number is set) are printed exactly as the server masked them.
 *
 * NOTHING IS DROPPED. The sections are the report's known shape; the whole
 * report is also available, field by field, under "Every field in the report",
 * so a field added to the server shows up here before anyone designs for it.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { apiFiscalHealth } from "../api/index.js";
import { AppButton, AppLinkButton } from "../components/ui";
import Loading from "../components/Loading";
import FiscalModeBadge, { FiscalNumber } from "../components/fiscal/FiscalModeBadge.js";
import TaxHelp from "../components/fiscal/taxHelp.js";
import {
  DeniedNotice,
  PAGE,
  Pill,
  StatusPill,
  errorText,
  fmtClock,
  fmtDateTime,
  fmtMoney,
  humaniseKey,
  TYPE,
} from "../components/fiscal/fiscalUi.js";

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const looksLikeMode = (key, value) =>
  /(mode|environment|env)$/i.test(String(key)) && /^(TEST|LIVE)$/i.test(String(value || "").trim());
const looksLikeDate = (key, value) =>
  typeof value === "string" && /(at|time|date|since|until|expires|expiry|checked)$/i.test(String(key)) && !Number.isNaN(Date.parse(value));

const muted = (text) => <span style={PAGE.muted}>{text}</span>;

/** One value of the raw report, printed as it came. */
const Value = ({ k, value, depth }) => {
  if (value === null || value === undefined || value === "") return muted("Not reported");
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (looksLikeMode(k, value)) return <FiscalModeBadge mode={value} />;
  if (looksLikeDate(k, value)) return <span>{fmtDateTime(value)}</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return muted("None");
    if (value.every((v) => !isPlainObject(v) && !Array.isArray(v))) {
      return <span style={{ whiteSpace: "pre-wrap" }}>{value.map(String).join(", ")}</span>;
    }
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {value.map((item, i) => (
          <div key={i} style={{ borderLeft: "2px solid #e2e8f0", paddingLeft: 10 }}>
            <Value k={`${k}[${i}]`} value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  if (isPlainObject(value)) {
    if (depth >= 3) return <pre style={{ ...PAGE.mono, margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(value, null, 2)}</pre>;
    return <KeyValues data={value} depth={depth + 1} />;
  }
  return <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{String(value)}</span>;
};

const KeyValues = ({ data, depth = 0 }) => (
  <div style={{ ...PAGE.detailGrid, gridTemplateColumns: depth === 0 ? "220px 1fr" : "180px 1fr" }}>
    {Object.entries(data).map(([k, v]) => (
      <React.Fragment key={k}>
        <div style={PAGE.detailLabel}>{humaniseKey(k)}</div>
        <div style={PAGE.detailValue}><Value k={k} value={v} depth={depth} /></div>
      </React.Fragment>
    ))}
  </div>
);

/* ── Layout pieces ─────────────────────────────────────────────────────── */

const S = {
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 },
  panel: { border: "1px solid #eef2f6", borderRadius: 12, padding: "16px 18px", background: "#fff" },
  panelTitle: { ...TYPE.body, fontWeight: 700, color: "#0f172a", margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 },
  rows: { ...TYPE.body, display: "grid", gridTemplateColumns: "170px 1fr", gap: "7px 14px", alignItems: "center" },
  label: { ...TYPE.secondary, color: "#64748b", fontWeight: 600 },
  value: { color: "#1e293b", overflowWrap: "anywhere" },
  stat: { display: "flex", flexDirection: "column", gap: 2, padding: "10px 12px", borderRadius: 10, background: "#f8fafc", minWidth: 0 },
  statValue: PAGE.figure,
  statLabel: PAGE.figureLabel,
};

const Panel = ({ title, icon, aside, children, testid, id }) => (
  <div style={S.panel} data-testid={testid} id={id}>
    <div style={{ ...S.panelTitle, justifyContent: "space-between" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {icon ? <i className={icon} aria-hidden="true" style={{ color: "#083860" }} /> : null}
        {title}
      </span>
      {aside || null}
    </div>
    {children}
  </div>
);

const Row = ({ label, children }) => (
  <>
    <div style={S.label}>{label}</div>
    <div style={S.value}>{children}</div>
  </>
);

const Stat = ({ value, label, tone }) => (
  <div style={{ ...S.stat, ...(tone === "bad" ? { background: "#fdf0f0" } : tone === "warn" ? { background: "#fff", boxShadow: "inset 4px 0 0 #F2C811" } : {}) }}>
    <span style={S.statValue}>{value ?? 0}</span>
    <span style={S.statLabel}>{label}</span>
  </div>
);

const yesNo = (value, { good = true } = {}) => (
  <Pill tone={Boolean(value) === good ? "good" : "bad"}>{value ? "Yes" : "No"}</Pill>
);
const setOrNot = (value) =>
  value && value !== "NOT SET" ? <span style={PAGE.mono}>{value}</span> : <Pill tone="bad">Not set</Pill>;

/** How each check reads, given the identity's mode. */
const checkState = (check, mode) => {
  if (check.ok) return { tone: "good", icon: "fa fa-check-circle", text: "Passes" };
  if (check.stage === "PRODUCTION" && mode !== "LIVE") return { tone: "neutral", icon: "fa fa-minus-circle", text: "Not yet" };
  return { tone: "bad", icon: "fa fa-times-circle", text: "Needs attention" };
};
const TONE_COLOUR = { good: "#047857", bad: "#b42318", neutral: "#64748b" };

/**
 * A check's next step: a dashboard page (`to`), URA's portal (`href`, in its
 * own tab) or a panel further down this report (`anchor`). The server decides
 * which (services/fiscal/fiscalReadinessReport.js); nothing is built here.
 */
const CheckAction = ({ action }) => {
  if (!action || !action.label) return null;
  const icon = action.anchor ? "fa fa-arrow-down" : action.href ? null : "fa fa-arrow-right";
  return (
    <AppLinkButton size="sm" icon={icon} to={action.to} href={action.href} anchor={action.anchor} data-testid="check-action">
      {action.label}
    </AppLinkButton>
  );
};

const Verdict = ({ verdict, mode }) => {
  const checks = Array.isArray(verdict?.checks) ? verdict.checks : [];
  const filing = checks.filter((c) => c.stage !== "PRODUCTION");
  const production = checks.filter((c) => c.stage === "PRODUCTION");
  const filingOk = filing.filter((c) => c.ok).length;
  const allFiling = filing.length > 0 && filingOk === filing.length;
  const headline = allFiling
    ? mode === "LIVE"
      ? "Lusuku is filing to URA in production."
      : "Filing to URA works in the test environment."
    : `${filing.length - filingOk} of ${filing.length} filing checks need attention.`;
  const percent = filing.length ? Math.round((filingOk / filing.length) * 100) : 0;

  const group = (title, list) =>
    list.length ? (
      <div>
        <div style={PAGE.sectionTitle}>{title}</div>
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((check) => {
            const state = checkState(check, mode);
            return (
              <div
                key={check.label}
                data-testid="health-check"
                data-state={state.tone}
                style={{ ...TYPE.body, display: "grid", gridTemplateColumns: "20px 1fr auto", gap: 10, alignItems: "start" }}
              >
                <i className={state.icon} aria-hidden="true" style={{ color: TONE_COLOUR[state.tone], marginTop: 3 }} />
                <div>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>{check.label}</div>
                  {!check.ok && check.fix ? <div style={{ ...PAGE.note, marginTop: 2 }}>{check.fix}</div> : null}
                  {/* The step that fixes it, as a control: where to go, from
                      the server, so the page and the report agree. */}
                  {!check.ok && check.action ? (
                    <div style={{ marginTop: 6 }}>
                      <CheckAction action={check.action} />
                    </div>
                  ) : null}
                </div>
                <Pill tone={state.tone}>{state.text}</Pill>
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  return (
    <div style={{ ...S.panel, padding: "20px 22px" }} data-testid="health-verdict">
      <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ ...TYPE.figure, color: allFiling ? "#047857" : "#0f172a" }} data-testid="health-score">
          {filingOk}/{filing.length}
        </div>
        <div style={{ flex: "1 1 260px" }}>
          <div style={{ ...TYPE.title, color: "#0f172a" }} data-testid="health-headline">
            {headline}
            <TaxHelp topic="healthVerdict" />
          </div>
          <div style={{ height: 8, borderRadius: 999, background: "#eef2f6", marginTop: 8, overflow: "hidden" }}>
            <div style={{ width: `${percent}%`, height: "100%", background: allFiling ? "#10b981" : "#F2C811" }} />
          </div>
          <div style={{ ...PAGE.note, marginTop: 6 }}>
            {verdict?.passed ?? 0} of {verdict?.total ?? checks.length} checks pass overall.
            {production.length && mode !== "LIVE" ? " The production checks wait for URA's sign off." : ""}
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 22 }}>
        {group("Filing", filing)}
        {group("Production", production)}
      </div>
    </div>
  );
};

const IDENTITY_SOURCE = {
  NEITHER:
    "No seller identity is configured where this server runs. Production reads it from its own settings, and a " +
    "document already issued keeps the identity frozen on it, so this alone does not mean filing is broken.",
};

/* ── The page ──────────────────────────────────────────────────────────── */

const FiscalHealthView = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiFiscalHealth();
      setReport(isPlainObject(data) ? data : { report: data });
      setFetchedAt(new Date());
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else setError(errorText(err, "Could not load the EFRIS health report."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!denied) load();
  }, [load, denied]);

  if (denied) {
    return <DeniedNotice what="The readiness report names the submitter's host, credentials and URA endpoints." />;
  }

  const mode = report?.mode || report?.identity?.mode || null;
  const id = report?.identity || {};
  const item = id.commission || {};
  const c = report?.commission || {};
  const sales = report?.sales || {};
  const sub = report?.submitter || {};
  const recent = Array.isArray(c.recent) ? c.recent : [];
  const rejected = Array.isArray(c.rejected) ? c.rejected : [];
  const known = report && isPlainObject(report.verdict);

  return (
    <div style={PAGE.page}>
      <div style={PAGE.card}>
        <div style={PAGE.cardHeader}>
          <div>
            <h4 style={PAGE.cardTitle}>
              EFRIS health
              <TaxHelp topic="fiscalDocument" />
              {mode ? <FiscalModeBadge mode={mode} size="md" /> : null}
              {mode ? <TaxHelp topic="environment" /> : null}
            </h4>
            <p style={PAGE.cardSubtitle}>
              Whether Lusuku can file its own commission invoices and credit notes with URA right now, and what to fix
              when it cannot. Masked values are shown exactly as the server masked them.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {fetchedAt ? (
              <span style={{ ...TYPE.caption, color: "#64748b" }} data-testid="fetched-at">Fetched at {fmtClock(fetchedAt)}</span>
            ) : null}
            <AppButton variant="outline" icon="fa fa-sync" onClick={load} disabled={loading}>Refresh</AppButton>
          </div>
        </div>

        <div style={PAGE.cardBody}>
          {loading ? <Loading size="panel" label="Asking the submitter..." /> : null}

          {!loading && error ? (
            <div className="lk-note lk-note--danger">
              <span>{error}</span>
              <AppButton size="sm" variant="quiet" onClick={load} style={{ marginLeft: "auto" }}>Retry</AppButton>
            </div>
          ) : null}

          {!loading && !error && report ? (
            <div style={{ display: "grid", gap: 18 }}>
              {known ? <Verdict verdict={report.verdict} mode={mode} /> : null}

              {known ? (
                <div style={S.grid}>
                  <Panel title={<>Lusuku as seller<TaxHelp topic="sellerIdentity" /></>} icon="fa fa-building" testid="health-identity" id="lusuku-as-seller"
                    aside={id.mode ? <FiscalModeBadge mode={id.mode} /> : null}>
                    {IDENTITY_SOURCE[id.source] ? (
                      <div className="lk-note lk-note--warning" style={{ ...TYPE.secondary, marginBottom: 12 }}>
                        <span>{IDENTITY_SOURCE[id.source]}</span>
                      </div>
                    ) : null}
                    <div style={S.rows}>
                      <Row label={<>Configured from<TaxHelp topic="configuredFrom" /></>}>{id.source ? humaniseKey(String(id.source).toLowerCase()) : muted("Not reported")}</Row>
                      <Row label="Can issue documents">{yesNo(id.issuable)}</Row>
                      <Row label="Legal name">{id.legalName || <Pill tone="bad">Not set</Pill>}</Row>
                      <Row label={<>TIN<TaxHelp topic="tin" /></>}>{setOrNot(id.tin)}</Row>
                      <Row label={<>Device number<TaxHelp topic="deviceNumber" /></>}>{id.deviceNumber === "set" ? <Pill tone="good">Set</Pill> : <Pill tone="bad">Not set</Pill>}</Row>
                      <Row label="Address">{yesNo(id.hasAddress)}</Row>
                      <Row label="Email">{yesNo(id.hasEmail)}</Row>
                      <Row label={<>VAT registered<TaxHelp topic="vatRegistered" /></>}>{yesNo(id.vatRegistered)}</Row>
                    </div>
                  </Panel>

                  <Panel title={<>Commission item at URA<TaxHelp topic="commissionItem" /></>} icon="fa fa-tag" testid="health-item">
                    <div style={S.rows}>
                      <Row label="Item code">{item.itemCode ? <span style={PAGE.mono}>{item.itemCode}</span> : <Pill tone="bad">Not set</Pill>}</Row>
                      <Row label="Item name">{item.itemName || <Pill tone="bad">Not set</Pill>}</Row>
                      <Row label="Commodity category">
                        {item.goodsCategoryId ? (
                          <span>
                            <span style={PAGE.mono}>{item.goodsCategoryId}</span>
                            {item.goodsCategoryName ? <span style={PAGE.meta}>{item.goodsCategoryName}</span> : null}
                          </span>
                        ) : <Pill tone="bad">Not set</Pill>}
                      </Row>
                      <Row label="Unit of measure">{item.unitOfMeasure ? <span style={PAGE.mono}>{item.unitOfMeasure}</span> : <Pill tone="bad">Not set</Pill>}</Row>
                    </div>
                  </Panel>

                  <Panel
                    title={<>Commission documents<TaxHelp topic="documentCounters" /></>}
                    icon="fa fa-file"
                    testid="health-queue"
                    aside={
                      <AppLinkButton size="sm" icon="fa fa-arrow-right" to="/admin/fiscal-documents">
                        Open Fiscal documents
                      </AppLinkButton>
                    }
                  >
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 12 }}>
                      <Stat value={c.total} label="Raised" />
                      <Stat value={c.withFdn} label="With an FDN" />
                      <Stat value={c.liveWithFdn} label="Live with an FDN" />
                      <Stat value={c.pending} label="Waiting to file" tone={c.pending ? "warn" : null} />
                      <Stat value={c.held} label="Held" tone={c.held ? "bad" : null} />
                      <Stat value={c.expiredSubmitting} label="Stuck mid-claim" tone={c.expiredSubmitting ? "bad" : null} />
                    </div>
                    {isPlainObject(c.byFiscalStatus) && Object.keys(c.byFiscalStatus).length ? (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                        {Object.entries(c.byFiscalStatus).map(([status, n]) => (
                          <span key={status} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <StatusPill status={status} /> <strong style={TYPE.secondary}>{n}</strong>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div style={S.rows}>
                      <Row label={`Settled orders, ${report.windowDays ?? "recent"} days`}>
                        {c.settledOrders?.count ?? 0}{c.settledOrders?.capped ? " (capped)" : ""}
                      </Row>
                      <Row label={<>Settled with no document<TaxHelp topic="settledOrders" /></>}>
                        {c.settledWithoutDocument?.count ? (
                          <Pill tone="bad">{c.settledWithoutDocument.count}</Pill>
                        ) : (
                          <Pill tone="good">None</Pill>
                        )}
                      </Row>
                    </div>
                  </Panel>

                  <Panel title={<>Submitter activity<TaxHelp topic="submitter" /></>} icon="fa fa-server" testid="health-submitter">
                    <div style={S.rows}>
                      <Row label="Last filed">
                        {sub.newestFiledCommission ? (
                          <span>
                            <FiscalNumber value={sub.newestFiledCommission.fdn} mode={sub.newestFiledCommission.mode} />
                            <span style={PAGE.meta}>
                              {sub.newestFiledCommission.invoiceNumber}, {fmtDateTime(sub.newestFiledCommission.submittedAt) || "time not recorded"}
                            </span>
                          </span>
                        ) : muted("Nothing filed yet")}
                      </Row>
                      <Row label="Last touched a document">
                        {sub.lastCommissionTouch ? (
                          <span>
                            {fmtDateTime(sub.lastCommissionTouch.at) || "time not recorded"}
                            <span style={PAGE.meta}>{sub.lastCommissionTouch.invoiceNumber}</span>
                          </span>
                        ) : muted("Never")}
                      </Row>
                      <Row label={<>Vendor sales filed<TaxHelp topic="vendorSalesFiled" /></>}>
                        {sales.withFdn ?? 0} of {sales.total ?? 0}
                      </Row>
                    </div>
                    {sub.note ? <div style={{ ...PAGE.note, marginTop: 12 }}>If filing stops: {sub.note}</div> : null}
                  </Panel>
                </div>
              ) : null}

              {known && rejected.length ? (
                <Panel
                  title="Rejected by URA"
                  icon="fa fa-exclamation-triangle"
                  testid="health-rejected"
                  aside={
                    <AppLinkButton size="sm" icon="fa fa-arrow-right" to="/admin/fiscal-documents?status=REJECTED">
                      Open in Fiscal documents
                    </AppLinkButton>
                  }
                >
                  <div style={{ display: "grid", gap: 10 }}>
                    {rejected.map((r) => (
                      <div key={r.invoiceNumber} style={TYPE.body}>
                        <div style={{ ...PAGE.mono, fontWeight: 700 }}>{r.invoiceNumber}</div>
                        <div style={{ color: "#b42318" }}>{r.error || "No reason recorded"}</div>
                        {r.uraDetail ? <div style={PAGE.note}>URA said: {r.uraDetail}</div> : null}
                      </div>
                    ))}
                  </div>
                </Panel>
              ) : null}

              {known ? (
                <Panel title={`Recent commission documents, last ${report.windowDays ?? ""} days`} icon="fa fa-list" testid="health-recent">
                  {recent.length === 0 ? (
                    <div style={PAGE.empty}>No commission documents in this window.</div>
                  ) : (
                    <div className="lk-table-frame">
                      <Table responsive className="lk-table" style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Document</th>
                            <th>Status</th>
                            <th>Fiscal document number</th>
                            <th style={{ textAlign: "right" }}>Amount</th>
                            <th>Issued</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recent.map((r) => (
                            <tr key={r.invoiceNumber}>
                              <td><span style={{ ...PAGE.mono, fontWeight: 600 }}>{r.invoiceNumber}</span></td>
                              <td>
                                <StatusPill status={r.fiscalStatus} />
                                {r.error ? <span className="lk-table-meta">{r.error}</span> : null}
                              </td>
                              <td><FiscalNumber value={r.fdn} mode={r.mode} /></td>
                              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmtMoney(r.gross)}</td>
                              <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(r.issuedAt) || muted("Not recorded")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Panel>
              ) : null}

              {known ? (
                <div>
                  <AppButton size="sm" variant="quiet" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
                    {showAll ? "Hide every field" : "Every field in the report"}
                  </AppButton>
                  {showAll ? (
                    <div style={{ ...S.panel, marginTop: 10 }} data-testid="health-raw">
                      <KeyValues data={report} />
                    </div>
                  ) : null}
                </div>
              ) : (
                /* A report without a verdict is not the shape this page was
                   designed for; show it whole rather than show nothing. */
                <div style={S.panel} data-testid="health-raw">
                  <KeyValues data={report} />
                </div>
              )}

              <div style={{ ...TYPE.caption, color: "#94a3b8" }}>
                Report generated {fmtDateTime(report.generatedAt) || "at an unreported time"}
                {report.country ? `, market ${report.country}` : ""}.
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FiscalHealthView;
